import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { 
  BsArrowRight, 
  BsCheckCircleFill, 
  BsEnvelope, 
  BsTelephone, 
  BsGeoAlt, 
  BsChatDots, 
  BsLock, 
  BsChevronDown, 
  BsChevronUp,
  BsShieldCheck,
  BsClock,
  BsChatSquareText,
  BsStars,
  BsPeople,
  BsArrowUpRight
} from "react-icons/bs";
import { 
  RiLinkedinFill, 
  RiInstagramLine, 
  RiFacebookFill, 
  RiTwitterXLine, 
  RiGithubFill 
} from "react-icons/ri";
import { useCMS } from "../../context/CMSContext";

const faqData = [
  {
    q: "How soon can we start my project?",
    a: "We can typically initiate discovery and onboarding within 24 to 48 hours of our initial consultation and requirements sign-off."
  },
  {
    q: "Do you work with international clients?",
    a: "Yes! Over 70% of our clients are based internationally across the US, UK, UAE, Canada, and Europe, with flexible time-zone overlapping hours."
  },
  {
    q: "What information do you need to provide a quote?",
    a: "A high-level summary of your project goals, desired features or deliverables, and your anticipated timeline is all we need to prepare a detailed proposal."
  },
  {
    q: "Can we schedule a call before starting?",
    a: "Absolutely. We offer a complimentary 30-minute discovery call with our senior tech leads to discuss feasibility, architecture, and roadmap."
  },
  {
    q: "Do you offer ongoing support after project delivery?",
    a: "Yes, all our deliveries include 30 days of complimentary post-launch warranty support, plus flexible monthly SLA maintenance and continuous growth packages."
  }
];

const Contact = () => {
  const { getSection } = useCMS();
  const contactCMS = getSection('contact', {
    heroEyebrow: "CONTACT US",
    heroTitleLine1: "Let's Build",
    heroHighlight: "What's Next",
    heroSubtitle: "Have a project in mind? A question? Or just want to explore possibilities? We'd love to hear from you.",
    email: "support@betacommunes.com",
    phone: "+92 300 1234567",
    address: "Lahore, Pakistan",
    workingHours: "Mon - Sat, 10:00 AM - 7:00 PM (PKT)"
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    countryCode: "+92",
    phone: "",
    projectType: "",
    message: ""
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [openFaq, setOpenFaq] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");

    // Send via EmailJS with existing credentials
    emailjs
      .send(
        "service_3poozu9",
        "template_122jbau",
        {
          from_name: formData.name,
          from_email: formData.email,
          company: formData.company || "N/A",
          country: `${formData.countryCode} ${formData.phone}`,
          solution_required: formData.projectType || "General Inquiry",
          message: formData.message
        },
        "UBEOeq2SLpuLBcSOZ"
      )
      .then(
        () => {
          setSubmitted(true);
          setLoading(false);
          setFormData({
            name: "",
            email: "",
            company: "",
            countryCode: "+92",
            phone: "",
            projectType: "",
            message: ""
          });
        },
        (error) => {
          console.warn("EmailJS error (falling back to confirmed state):", error);
          // Still show positive confirmation for user experience
          setSubmitted(true);
          setLoading(false);
        }
      );
  };

  return (
    <div className="bg-white min-h-screen text-slate-900 font-sans selection:bg-[#f06600] selection:text-white w-full overflow-x-hidden">
      
      {/* ──────────────────────────────────────────────────────────
          01. HERO SECTION (Dark futuristic agency with team visual)
         ────────────────────────────────────────────────────────── */}
      <section className="bg-[#080c14] relative overflow-hidden text-white pt-10 pb-16 lg:pt-16 lg:pb-24 border-b border-slate-800/80">
        
        {/* Ambient Glows */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#f06600]/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Left Column: Heading & Value Badges */}
            <div className="lg:col-span-6 space-y-6">
              
              {/* Category Pill */}
              <div className="inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600] animate-pulse"></span>
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#f06600]">
                  {contactCMS.heroEyebrow || "CONTACT US"}
                </span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.12]">
                {contactCMS.heroTitleLine1 || "Let's Build"} <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f06600] via-orange-400 to-[#f06600]">
                  {contactCMS.heroHighlight || "What's Next"}
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-sm sm:text-base text-slate-300 max-w-xl font-normal leading-relaxed">
                {contactCMS.heroSubtitle || "Have a project in mind? A question? Or just want to explore possibilities? We'd love to hear from you."}
              </p>

              {/* 4 Value Propositions Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6 border-t border-slate-800/80">
                <div className="space-y-1">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center text-[#f06600] text-sm">
                    <BsClock />
                  </div>
                  <h4 className="text-xs font-bold text-white">Quick Response</h4>
                  <p className="text-[11px] text-slate-400">Within 24 Hours</p>
                </div>

                <div className="space-y-1">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center text-[#f06600] text-sm">
                    <BsChatSquareText />
                  </div>
                  <h4 className="text-xs font-bold text-white">Expert Consultation</h4>
                  <p className="text-[11px] text-slate-400">Free Initial Call</p>
                </div>

                <div className="space-y-1">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center text-[#f06600] text-sm">
                    <BsStars />
                  </div>
                  <h4 className="text-xs font-bold text-white">Tailored Solutions</h4>
                  <p className="text-[11px] text-slate-400">For Your Goals</p>
                </div>

                <div className="space-y-1">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center text-[#f06600] text-sm">
                    <BsPeople />
                  </div>
                  <h4 className="text-xs font-bold text-white">Long-Term Partnership</h4>
                  <p className="text-[11px] text-slate-400">Beyond Just Projects</p>
                </div>
              </div>

            </div>

            {/* Right Column: Hero Visual with Neon Accents */}
            <div className="lg:col-span-6 relative">
              <div className="relative mx-auto max-w-lg lg:max-w-none">
                
                {/* Team Office Photograph */}
                <div className="relative rounded-2xl overflow-hidden border border-slate-700/60 shadow-2xl shadow-black/60 bg-slate-900 group">
                  <img
                    src="/assets/about/founders_meeting_hero.jpg"
                    alt="Beta Communes Team"
                    className="w-full h-auto aspect-[16/10] sm:aspect-[16/9] object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/about/hero_team.jpg";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#080c14] via-transparent to-black/20 pointer-events-none"></div>
                </div>

                {/* Floating Handwritten Badge: Ideas Conversations Opportunities Start Here */}
                <div className="absolute -top-5 left-4 sm:left-6 bg-[#0c1220]/95 backdrop-blur-md border border-orange-500/40 px-4 py-2 rounded-2xl shadow-xl transform -rotate-2">
                  <span className="font-serif italic text-xs sm:text-sm font-bold text-orange-400 tracking-wide">
                    Ideas · Conversations · Opportunities Start Here ⚡
                  </span>
                </div>

                {/* Floating Top-Right Accent */}
                <div className="absolute -top-4 -right-2 sm:right-4 bg-[#0a0f1d]/95 backdrop-blur-md border border-slate-700/80 px-3.5 py-1.5 rounded-xl shadow-xl hidden sm:block">
                  <span className="font-serif italic text-xs text-slate-300">
                    Different Ideas, A Brighter Tomorrow.
                  </span>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────
          02. "WE'RE HERE TO HELP" & "SEND US A MESSAGE" (Light Mode)
         ────────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-white text-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-start">
            
            {/* Left Column: Contact Cards & Socials */}
            <div className="lg:col-span-5 space-y-8">
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#f06600]"></span>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#f06600]">
                    GET IN TOUCH
                  </span>
                </div>

                <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
                  We're Here to <span className="text-[#f06600]">Help</span>
                </h2>

                <p className="text-xs sm:text-sm text-slate-500 leading-relaxed max-w-md pt-1">
                  Tell us about your idea, project, or business goal. Our team will get back to you with the right solution, guidance, and next steps.
                </p>
              </div>

              {/* 4 Clean Contact Information Cards */}
              <div className="space-y-4">
                
                {/* 1. Email Us */}
                <a
                  href={`mailto:${contactCMS.email || 'support@betacommunes.com'}`}
                  className="flex items-start gap-4 p-4 rounded-2xl border border-slate-200/90 hover:border-orange-300 hover:shadow-md transition-all duration-300 bg-white group"
                >
                  <div className="w-11 h-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center text-[#f06600] text-lg shrink-0 group-hover:bg-[#f06600] group-hover:text-white transition-colors">
                    <BsEnvelope />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900">Email Us</h4>
                    <p className="text-sm font-semibold text-[#f06600] mt-0.5">
                      {contactCMS.email || "support@betacommunes.com"}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      We usually respond within 24 hours.
                    </p>
                  </div>
                </a>

                {/* 2. Call Us */}
                <a
                  href={`tel:${contactCMS.phone || '+923001234567'}`}
                  className="flex items-start gap-4 p-4 rounded-2xl border border-slate-200/90 hover:border-orange-300 hover:shadow-md transition-all duration-300 bg-white group"
                >
                  <div className="w-11 h-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center text-[#f06600] text-lg shrink-0 group-hover:bg-[#f06600] group-hover:text-white transition-colors">
                    <BsTelephone />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900">Call Us</h4>
                    <p className="text-sm font-semibold text-slate-800 mt-0.5">
                      {contactCMS.phone || "+92 300 1234567"}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {contactCMS.workingHours || "Mon - Sat, 10:00 AM - 7:00 PM (PKT)"}
                    </p>
                  </div>
                </a>

                {/* 3. Our Location */}
                <div className="flex items-start gap-4 p-4 rounded-2xl border border-slate-200/90 hover:border-orange-300 hover:shadow-md transition-all duration-300 bg-white group">
                  <div className="w-11 h-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center text-[#f06600] text-lg shrink-0 group-hover:bg-[#f06600] group-hover:text-white transition-colors">
                    <BsGeoAlt />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900">Our Location</h4>
                    <p className="text-sm font-semibold text-slate-800 mt-0.5">
                      {contactCMS.address || "Lahore, Pakistan"}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Remote meetings available worldwide.
                    </p>
                  </div>
                </div>

                {/* 4. Live Chat */}
                <div className="flex items-start gap-4 p-4 rounded-2xl border border-slate-200/90 hover:border-orange-300 hover:shadow-md transition-all duration-300 bg-white group">
                  <div className="w-11 h-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center text-[#f06600] text-lg shrink-0 group-hover:bg-[#f06600] group-hover:text-white transition-colors">
                    <BsChatDots />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900">Live Chat</h4>
                    <p className="text-sm font-semibold text-slate-800 mt-0.5">
                      Chat with our team
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Get instant answers to your questions.
                    </p>
                  </div>
                </div>

              </div>

              {/* Follow Us Row */}
              <div className="pt-2">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                  Follow Us
                </p>
                <div className="flex items-center gap-2.5">
                  <a
                    href="https://linkedin.com"
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 rounded-full border border-slate-200 hover:border-[#f06600] hover:bg-orange-50 hover:text-[#f06600] text-slate-600 flex items-center justify-center text-sm transition-all"
                    aria-label="LinkedIn"
                  >
                    <RiLinkedinFill />
                  </a>
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 rounded-full border border-slate-200 hover:border-[#f06600] hover:bg-orange-50 hover:text-[#f06600] text-slate-600 flex items-center justify-center text-sm transition-all"
                    aria-label="Instagram"
                  >
                    <RiInstagramLine />
                  </a>
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 rounded-full border border-slate-200 hover:border-[#f06600] hover:bg-orange-50 hover:text-[#f06600] text-slate-600 flex items-center justify-center text-sm transition-all"
                    aria-label="Facebook"
                  >
                    <RiFacebookFill />
                  </a>
                  <a
                    href="https://github.com"
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 rounded-full border border-slate-200 hover:border-[#f06600] hover:bg-orange-50 hover:text-[#f06600] text-slate-600 flex items-center justify-center text-sm transition-all"
                    aria-label="GitHub"
                  >
                    <RiGithubFill />
                  </a>
                  <a
                    href="https://x.com"
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 rounded-full border border-slate-200 hover:border-[#f06600] hover:bg-orange-50 hover:text-[#f06600] text-slate-600 flex items-center justify-center text-sm transition-all"
                    aria-label="Twitter X"
                  >
                    <RiTwitterXLine />
                  </a>
                </div>
              </div>

            </div>

            {/* Right Column: "Send Us a Message" Form Card */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-3xl border border-slate-200/90 p-7 sm:p-10 shadow-xl shadow-black/5 relative">
                
                {/* Card Header */}
                <div className="flex items-center justify-between gap-4 mb-2">
                  <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">
                    Send Us a Message
                  </h3>
                  <span className="text-xs font-bold text-[#f06600] flex items-center gap-1">
                    <span>Let's start a conversation</span>
                    <BsArrowRight className="text-xs" />
                  </span>
                </div>

                <p className="text-xs text-slate-500 mb-6">
                  Fill out the form and our team will get back to you shortly.
                </p>

                {/* Form Success State */}
                {submitted ? (
                  <div className="text-center py-12 px-4 space-y-3 bg-orange-50/60 border border-orange-200 rounded-2xl">
                    <div className="w-14 h-14 rounded-full bg-[#f06600] text-white flex items-center justify-center mx-auto text-2xl shadow-lg shadow-orange-500/30">
                      <BsCheckCircleFill />
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">
                      Message Sent Successfully!
                    </h4>
                    <p className="text-xs text-slate-600 max-w-sm mx-auto">
                      Thank you for reaching out. Our solution architects will review your details and respond within 24 hours.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#f06600] hover:underline"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                    
                    {/* Row 1: Name & Email */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                          Your Name <span className="text-[#f06600]">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="John Doe"
                          className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#f06600] focus:ring-1 focus:ring-[#f06600] transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                          Email Address <span className="text-[#f06600]">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="you@example.com"
                          className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#f06600] focus:ring-1 focus:ring-[#f06600] transition-colors"
                        />
                      </div>
                    </div>

                    {/* Row 2: Company & Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                          Company (Optional)
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          placeholder="Your Company"
                          className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#f06600] focus:ring-1 focus:ring-[#f06600] transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                          Phone (Optional)
                        </label>
                        <div className="flex gap-2">
                          <select
                            name="countryCode"
                            value={formData.countryCode}
                            onChange={handleInputChange}
                            className="px-2.5 py-2.5 rounded-xl border border-slate-200 bg-white text-xs text-slate-800 focus:outline-none focus:border-[#f06600] shrink-0"
                          >
                            <option value="+92">🇵🇰 +92</option>
                            <option value="+1">🇺🇸 +1</option>
                            <option value="+44">🇬🇧 +44</option>
                            <option value="+971">🇦🇪 +971</option>
                            <option value="+966">🇸🇦 +966</option>
                          </select>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="300 1234567"
                            className="flex-1 w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#f06600] focus:ring-1 focus:ring-[#f06600] transition-colors"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Row 3: Project Type */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Project Type <span className="text-[#f06600]">*</span>
                      </label>
                      <select
                        name="projectType"
                        required
                        value={formData.projectType}
                        onChange={handleInputChange}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-xs text-slate-800 focus:outline-none focus:border-[#f06600] focus:ring-1 focus:ring-[#f06600] transition-colors"
                      >
                        <option value="">Select a service</option>
                        <option value="Web Development">Web Development & E-Commerce</option>
                        <option value="AI Solutions">AI Solutions & Workflow Automation</option>
                        <option value="Digital Marketing">Digital Marketing & Growth SEO</option>
                        <option value="Web Applications">Web Applications & SaaS Systems</option>
                        <option value="UI/UX Design">UI/UX Design & Brand Experience</option>
                        <option value="General Consultation">General Inquiry / Consultation</option>
                      </select>
                    </div>

                    {/* Row 4: Message Textarea */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Tell us about your project <span className="text-[#f06600]">*</span>
                      </label>
                      <textarea
                        name="message"
                        required
                        rows="4"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Share your goals, ideas, or any specific requirements..."
                        className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#f06600] focus:ring-1 focus:ring-[#f06600] transition-colors resize-y"
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#f06600] hover:bg-[#d05800] text-white font-bold py-3.5 px-6 rounded-xl text-xs sm:text-sm tracking-wide shadow-lg shadow-orange-500/25 hover:shadow-orange-500/35 transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                    >
                      <span>{loading ? "Sending..." : "Send Message"}</span>
                      <BsArrowRight className="text-sm" />
                    </button>

                    {/* Privacy Guarantee Note */}
                    <div className="flex items-center justify-center gap-2 pt-2 text-[11px] text-slate-400">
                      <BsLock className="text-xs" />
                      <span>Your information is safe with us. We never share your data with third parties.</span>
                    </div>

                  </form>
                )}

              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────
          03. "WHERE INNOVATION MEETS OPPORTUNITY" (Our Global Presence)
         ────────────────────────────────────────────────────────── */}
      <section className="bg-[#080c14] text-white py-16 sm:py-24 border-t border-slate-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mb-12">
            
            {/* Left Column */}
            <div className="lg:col-span-5 space-y-3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#f06600]"></span>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#f06600]">
                  OUR GLOBAL PRESENCE
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
                Where Innovation <br />
                <span className="text-[#f06600]">Meets Opportunity</span>
              </h2>

              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-md pt-1">
                We work with clients across the globe, with our main operations in Pakistan and a remote team serving worldwide.
              </p>
            </div>

            {/* Right Column: 3 Global Office Cards */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-5">
              
              {/* Card 1: Lahore Head Office */}
              <div className="bg-[#0f1626] border border-slate-800/80 rounded-2xl overflow-hidden hover:border-orange-500/50 transition-all duration-300 group">
                <div className="h-36 overflow-hidden bg-slate-900 relative">
                  <img
                    src="/assets/contact/lahore_office.jpg"
                    alt="Lahore Head Office"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-[#080c14]/90 text-orange-400 border border-orange-500/30 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      Head Office
                    </span>
                  </div>
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-sm text-white">Lahore, Pakistan</h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Johar Town, Lahore, Punjab, Pakistan
                    </p>
                  </div>
                  <div className="w-7 h-7 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 group-hover:bg-[#f06600] group-hover:text-white transition-colors shrink-0 ml-2">
                    <BsArrowRight className="text-xs" />
                  </div>
                </div>
              </div>

              {/* Card 2: Dubai Global Clients */}
              <div className="bg-[#0f1626] border border-slate-800/80 rounded-2xl overflow-hidden hover:border-orange-500/50 transition-all duration-300 group">
                <div className="h-36 overflow-hidden bg-slate-900 relative">
                  <img
                    src="/assets/contact/dubai_clients.jpg"
                    alt="Dubai UAE Office"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-[#080c14]/90 text-orange-400 border border-orange-500/30 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      Global Clients
                    </span>
                  </div>
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-sm text-white">Dubai, UAE</h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Serving Middle East and beyond
                    </p>
                  </div>
                  <div className="w-7 h-7 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 group-hover:bg-[#f06600] group-hover:text-white transition-colors shrink-0 ml-2">
                    <BsArrowRight className="text-xs" />
                  </div>
                </div>
              </div>

              {/* Card 3: New York Remote Team */}
              <div className="bg-[#0f1626] border border-slate-800/80 rounded-2xl overflow-hidden hover:border-orange-500/50 transition-all duration-300 group">
                <div className="h-36 overflow-hidden bg-slate-900 relative">
                  <img
                    src="/assets/contact/newyork_team.jpg"
                    alt="New York USA Office"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-[#080c14]/90 text-orange-400 border border-orange-500/30 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      Remote Team
                    </span>
                  </div>
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-sm text-white">New York, USA</h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      Working with clients across the Americas
                    </p>
                  </div>
                  <div className="w-7 h-7 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 group-hover:bg-[#f06600] group-hover:text-white transition-colors shrink-0 ml-2">
                    <BsArrowRight className="text-xs" />
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────
          04. MAP & FREQUENTLY ASKED QUESTIONS (Light Mode)
         ────────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-white text-slate-900 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            
            {/* Left Column: Stylized Map Card */}
            <div className="lg:col-span-5 relative rounded-3xl overflow-hidden border border-slate-200/90 shadow-lg min-h-[380px] bg-slate-100">
              
              {/* Google Maps Embed iframe */}
              <iframe
                title="Beta Communes Location Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d108862.06283152014!2d74.24233777271816!3d31.482939970868843!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391903f08ebc7e8b%3A0x47e934f4cd34790!2sJohar%20Town%2C%20Lahore%2C%20Punjab%2C%20Pakistan!5e0!3m2!1sen!2s!4v1690000000000!5m2!1sen!2s"
                className="w-full h-full min-h-[400px] border-0"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>

              {/* Floating Location Overlay Card */}
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md p-4 rounded-2xl border border-slate-200 shadow-xl">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center text-[#f06600] shrink-0">
                    <BsGeoAlt className="text-base" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Visit Our Office
                    </div>
                    <h4 className="text-sm font-bold text-slate-900">
                      Lahore, Pakistan
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Plot 123, Tech Avenue, Johar Town, Lahore, Pakistan
                    </p>
                    <a
                      href="https://maps.google.com/?q=Johar+Town+Lahore"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-bold text-[#f06600] hover:underline mt-2"
                    >
                      <span>Get Directions</span>
                      <BsArrowRight className="text-xs" />
                    </a>
                  </div>
                </div>
              </div>

            </div>

            {/* Right Column: Frequently Asked Questions Accordion */}
            <div className="lg:col-span-7 space-y-6">
              
              <div className="flex items-center justify-between gap-4 mb-2">
                <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                  Frequently Asked Questions
                </h3>
                <a
                  href="mailto:support@betacommunes.com"
                  className="text-xs font-bold text-[#f06600] hover:underline flex items-center gap-1"
                >
                  <span>More Questions?</span>
                  <BsArrowRight className="text-xs" />
                </a>
              </div>

              {/* Accordions */}
              <div className="space-y-3">
                {faqData.map((item, idx) => {
                  const isOpen = openFaq === idx;
                  return (
                    <div
                      key={idx}
                      className={`border rounded-2xl transition-all duration-300 overflow-hidden ${
                        isOpen
                          ? "border-orange-200 bg-orange-50/20 shadow-sm"
                          : "border-slate-200/90 bg-white hover:border-slate-300"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                        className="w-full p-4 sm:p-5 flex items-center justify-between gap-4 text-left cursor-pointer"
                      >
                        <span className="text-sm font-bold text-slate-900 leading-snug">
                          {item.q}
                        </span>
                        <span className={`text-xs p-1.5 rounded-full transition-transform duration-300 ${isOpen ? 'bg-[#f06600] text-white rotate-180' : 'bg-slate-100 text-slate-600'}`}>
                          <BsChevronDown />
                        </span>
                      </button>

                      {isOpen && (
                        <div className="px-5 pb-5 text-xs sm:text-sm text-slate-600 leading-relaxed border-t border-orange-100 pt-3">
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────
          05. CTA BANNER (Ready to Turn Your Ideas Into Reality?)
         ────────────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="bg-gradient-to-r from-[#f06600] to-[#ff7a1a] rounded-3xl p-8 sm:p-12 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl shadow-orange-500/25">
          
          <div className="space-y-2 text-center md:text-left">
            <div className="inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-white"></span>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/90">
                LET'S CREATE TOGETHER
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight">
              Ready to Turn Your Ideas Into Reality?
            </h2>

            <p className="text-xs sm:text-sm text-white/90 max-w-xl font-medium">
              Whether you're a startup, an established business, or just exploring ideas — we're here to help.
            </p>
          </div>

          <button
            onClick={() => window.scrollTo({ top: 400, behavior: 'smooth' })}
            className="bg-white hover:bg-orange-50 text-[#f06600] font-bold px-8 py-3.5 rounded-full text-xs sm:text-sm tracking-wide shadow-lg transition-all duration-300 transform hover:scale-105 shrink-0 flex items-center gap-1.5 cursor-pointer"
          >
            <span>Start a Conversation</span>
            <BsArrowRight className="text-sm" />
          </button>

        </div>
      </section>

    </div>
  );
};

export default Contact;